// components/markdown/Tooltip.tsx

import React, { useState, useId } from 'react';
import { twMerge } from 'tailwind-merge';
// BaseCustomProps includes [key: string]: unknown;
import type { BaseCustomProps } from './types';

// Define the expected position values for validation
type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
const validPositions: TooltipPosition[] = ['top', 'bottom', 'left', 'right'];

// Component signature directly matches the required type FC<BaseCustomProps>
export const Tooltip: React.FC<BaseCustomProps> = (props) => {
    // Destructure known props from BaseCustomProps if needed elsewhere
    const { className, children } = props;

    // --- Access specific props and VALIDATE/CAST from 'unknown' ---

    // Validate 'text': must exist and be a non-empty string
    const text = (typeof props.text === 'string' && props.text)
        ? props.text
        : undefined;

    // Validate 'position': must be a string and one of the allowed values
    const position: TooltipPosition =
        (typeof props.position === 'string' && validPositions.includes(props.position as TooltipPosition))
        ? props.position as TooltipPosition
        : 'top'; // Default to 'top' if invalid or missing

    // Validate 'tooltipClassName': must be a string
    const tooltipClassName = (typeof props.tooltipClassName === 'string')
        ? props.tooltipClassName
        : undefined;

    // --- Component State and Hooks ---
    // Hooks must be called before any conditional returns
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const tooltipId = useId(); // React 18+ hook
    
    // --- Check for REQUIRED 'text' prop after validation ---
    if (text === undefined) {
        // Log a warning during development for easier debugging
        if (process.env.NODE_ENV === 'development') {
             console.warn(
                `Tooltip component: 'text' prop is missing, empty, or not a string. ` +
                `Received: ${JSON.stringify(props.text)}. Rendering children without tooltip.`
             );
        }
        // Render children directly without tooltip functionality - avoids breaking layout
        return <>{children}</>;
    }

    // --- Event Handlers ---
    const showTooltip = (e: React.MouseEvent<HTMLSpanElement> | React.FocusEvent<HTMLSpanElement>) => {
        const element = e.currentTarget;
        const rect = element.getBoundingClientRect();
        const spacing = 8;
        let top = 0;
        let left = 0;

        // Use the validated 'position' variable
        switch (position) {
            case 'bottom':
                top = rect.bottom + window.scrollY + spacing;
                left = rect.left + window.scrollX + rect.width / 2;
                break;
            case 'left':
                top = rect.top + window.scrollY + rect.height / 2;
                left = rect.left + window.scrollX - spacing;
                break;
            case 'right':
                top = rect.top + window.scrollY + rect.height / 2;
                left = rect.right + window.scrollX + spacing;
                break;
            case 'top':
            default:
                top = rect.top + window.scrollY - spacing;
                left = rect.left + window.scrollX + rect.width / 2;
                break;
        }
        setTooltipPosition({ top, left });
        setIsVisible(true);
    };

    const hideTooltip = () => {
        setIsVisible(false);
    };

    // --- Class Definitions ---
    const triggerClasses = twMerge(
        "inline-block relative",
        "border-b border-dotted border-neutral-500 dark:border-neutral-400", // Indicator
        "cursor-help", // Cursor hint
        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded-sm", // Focus style
        className // Pass className from BaseCustomProps
    );

    const tooltipBaseClasses = twMerge(
        "fixed z-50 px-3 py-1.5 text-sm rounded-md shadow-lg", // Base styles
        "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900", // Colors
        "transition-opacity duration-200", // Fade effect
        "pointer-events-none", // Prevent tooltip from blocking mouse events
        isVisible ? "opacity-100 visible" : "opacity-0 invisible", // Visibility toggle
        // Positioning transforms based on validated 'position'
        position === 'top' && "-translate-x-1/2 -translate-y-full",
        position === 'bottom' && "-translate-x-1/2 translate-y-2", // Adjusted for arrow
        position === 'left' && "-translate-x-full -translate-y-1/2 -ml-2", // Adjusted for arrow
        position === 'right' && "translate-x-2 -translate-y-1/2", // Adjusted for arrow
        tooltipClassName // Pass validated tooltipClassName
    );

    const arrowClasses = twMerge(
        "absolute w-2 h-2 bg-inherit transform rotate-45", // Arrow styling
        // Arrow positioning based on validated 'position'
        position === 'top'    && "bottom-0 left-1/2 -translate-x-1/2 translate-y-1",
        position === 'bottom' && "top-0 left-1/2 -translate-x-1/2 -translate-y-1",
        position === 'left'   && "right-0 top-1/2 -translate-y-1/2 translate-x-1",
        position === 'right'  && "left-0 top-1/2 -translate-y-1/2 -translate-x-1"
    );

    // --- Render ---
    return (
        <>
            <span
                className={triggerClasses}
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}
                onFocus={showTooltip} // Keyboard accessible
                onBlur={hideTooltip}  // Keyboard accessible
                tabIndex={0}          // Make focusable
                aria-describedby={tooltipId} // Accessibility for screen readers
            >
                {children} {/* Render children passed via BaseCustomProps */}
            </span>
            {/* Tooltip structure */}
            <span
                id={tooltipId}
                className={tooltipBaseClasses}
                style={{
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                }}
                role="tooltip" // ARIA role
            >
                {text} {/* Render the validated 'text' */}
                {/* Arrow element */}
                <span className={arrowClasses} style={{ background: 'inherit' }} />
            </span>
        </>
    );
};

// Optional: Add display name for easier debugging in React DevTools
Tooltip.displayName = 'Tooltip';